import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { PageHeader, Panel, StatTile, Avatar, height, fmt, shortDate, positionsOf } from "../../components/app/kit";
import { DEMO_TEAM, DEMO_PLAYERS, DEMO_GAMES, DEMO_RECORD } from "../../lib/demoData";
import { editionOf } from "../../components/app/TradingCard";

const EDITION = { gold: ["Gold", "#F5C542"], platinum: ["Platinum", "#E5E7EB"] };

const Leaders = () => {
  const top = (k) => [...DEMO_PLAYERS].sort((a, b) => b.season[k] - a.season[k])[0];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile testid="demo-record" label="Record" value={`${DEMO_RECORD.w}-${DEMO_RECORD.l}`} accent />
      {[["ppg", "Points"], ["rpg", "Rebounds"], ["apg", "Assists"]].map(([k, l]) => {
        const p = top(k);
        return <StatTile key={k} testid={`demo-leader-${k}`} label={`${l} leader`} value={`${fmt(p.season[k])} \u00b7 ${p.name.split(" ")[1]}`} />;
      })}
    </div>
  );
};

const RosterRow = ({ p }) => {
  const ed = EDITION[editionOf(p.season).tier];
  return (
    <Link to={`/demo/players/${p.id}`} data-testid={`demo-roster-${p.id}`} className="group flex items-center gap-4 rounded-2xl border border-[#1E293B] bg-[#0F172A] p-4 transition-colors hover:border-[#39FF14]/50">
      <Avatar name={p.name} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-white">#{p.jersey} {p.name}</p>
        <p className="truncate text-sm text-slate-400">{positionsOf(p)} · {height(p.height_in)} · Class of {p.grad_year}</p>
      </div>
      <div className="hidden text-right font-mono-gd text-sm sm:block">
        <p className="text-[#39FF14]">{fmt(p.season.ppg)} PPG</p>
        <p className="text-slate-400">{fmt(p.season.rpg)} RPG · {fmt(p.season.apg)} APG</p>
      </div>
      {ed && <span className="rounded-sm px-2 py-0.5 font-mono-gd text-[10px] font-bold uppercase text-[#0A0E1A]" style={{ background: ed[1] }}>{ed[0]}</span>}
      <ChevronRight className="h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
};

const GameRow = ({ g }) => {
  const win = g.us > g.them;
  return (
    <div data-testid={`demo-game-${g.id}`} className="flex items-center justify-between border-t border-[#1E293B] py-3 first:border-t-0">
      <div>
        <p className="font-semibold text-white">{g.home ? "vs" : "@"} {g.opponent}</p>
        <p className="text-sm text-slate-400">{shortDate(g.date)}</p>
      </div>
      <p className="font-mono-gd text-lg"><span className={win ? "text-[#39FF14]" : "text-red-300"}>{win ? "W" : "L"}</span> <span className="text-white">{g.us}-{g.them}</span></p>
    </div>
  );
};

export default function DemoTeam() {
  return (
    <div data-testid="demo-team-page">
      <PageHeader eyebrow={`Sample \u00b7 ${DEMO_TEAM.level} \u00b7 ${DEMO_TEAM.coach}`} title={DEMO_TEAM.name} />
      <div className="mb-8"><Leaders /></div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Panel>
          <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Roster</h3>
          <p className="-mt-2 mb-4 text-sm text-slate-400">Tap a player to open the full report coaches and recruiters see.</p>
          <div className="space-y-3">{DEMO_PLAYERS.map((p) => <RosterRow key={p.id} p={p} />)}</div>
        </Panel>
        <Panel>
          <h3 className="mb-2 font-display text-xl font-bold uppercase text-white">Games</h3>
          {DEMO_GAMES.map((g) => <GameRow key={g.id} g={g} />)}
        </Panel>
      </div>
    </div>
  );
}
